import { styled } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ObjectId } from "bson";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../models";
import StateInterface from "../../../redux-magic/state-interface";
import { PropertySnackbarInterface } from "../../../redux-magic/sub-interfaces/property-snackbar";
import { addPropertyImagesThunk, setPropertySnackbarThunk } from "../../../redux-magic/thunks";

/* Component Imports */

import {
	Alert,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	IconButton,
	MenuItem,
	RadioGroup,
	Select,
	SelectChangeEvent,
	Snackbar,
	SvgIcon,
	Typography,
	useTheme,
} from "@mui/material";
import { useRouter } from "next/router";
import BeegruButton from "../../common-components/buttons/BeegruButton";

/* Icon Imports */

import { Close, DeleteOutline } from "@mui/icons-material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RequestImagesIcon from "../../../public/images/icons/singleproperty/request_image.svg";

/* Library Function Imports */

import uploadFiles from "../../../lib/uploadFiles";
import validateFiles from "../../../lib/validateFiles";

/* Interfaces */

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

interface FileAndMore {
	file: File;
	mime_type: string;
}

/* Styled Components */

const CustomDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		[theme.breakpoints.down("sm")]: {
			borderRadius: "0rem",
			minHeight: "100%",
		},
		minHeight: "700px",
		maxHeight: "100%",
		borderRadius: "1rem",
		background: theme.palette.background.paper,
		position: "relative",
	},
}));

const CustomDialogTitle = styled(DialogTitle)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	padding: "0.5rem 1rem 0.5rem 1rem",
	background: theme.palette.background.default,
}));

const CustomDialogContent = styled(DialogContent)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	gap: "1rem",
	"&.MuiDialogContent-root": {
		padding: "1.5rem 2rem 1.5rem 2rem",
	},
	background: theme.palette.background.default,
}));

const CustomDialogActions = styled(DialogActions)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-end",
	alignItems: "center",
	width: "100%",
	gap: "1rem",
	padding: "0rem 1rem 1rem 0rem",
	background: theme.palette.background.default,
}));

const InputContent = styled("input")(({ theme }) => ({
	display: "none",
}));

const UploadContent = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	border: "2px dashed #EE4B2B",
	borderRadius: "16px",
	cursor: "pointer",
	borderColor: theme.palette.primary.main,
	height: "10rem",
}));

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "15rem",
	height: "10.5rem",
	borderRadius: "16px",
	borderColor: theme.palette.primary.main,
}));

// const SecondaryUploadContent = styled("div")(({ theme }) => ({
// 	display: "flex",
// 	flexDirection: "column",
// 	justifyContent: "center",
// 	alignItems: "center",
// 	width: "16rem",
// 	height: "10.238rem",
// 	border: "2px dashed #EE4B2B",
// 	borderRadius: "16px",
// 	cursor: "pointer",
// 	borderColor: theme.palette.primary.main,
// }));

const ImageContainer = styled("div")(({ theme }) => ({
	position: "relative",
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "15rem",
}));

const PreviewImage = styled("img")(({ theme }) => ({
	width: "16rem",
	height: "10.238rem",
	borderRadius: "16px ",
	// border: "3px solid rgba(0, 0, 0, 0.23)",
	// borderColor: theme.palette.primary.main,
}));

const Snack = styled(Snackbar)(({ theme }) => ({
	// [theme.breakpoints.down("md")]: {
	// 	bottom: "5rem",
	// 	borderRadius: "0.5rem",
	// },
}));

const FormControlContainer = styled(FormControl)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	padding: "var(--Spacing-spacing-sm, 0.5rem)",
	gap: "var(--Spacing-spacing-sm, 0.5rem)",
	alignSelf: "stretch",
	width: "100%",
	height: "3rem",
	position: "absolute",
	bottom: "0.3rem",
}));

const ProgressContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	position: "absolute",
	width: "100%",
	height: "100%",
	zIndex: 0,
}));

const ImagePreviewContainer = styled(RadioGroup)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	maxHeight: "30rem",
	gap: "1.5rem",
	flexWrap: "wrap",
}));

const UploadImageModal = ({
	open,
	handleClose,
	propertyData,
	index,
	session,
	dispatch,
	isEditor,
	profile_context,
	property_snackbar,
}: {
	open: boolean;
	handleClose: any;
	propertyData: LocalPropertyInterface;
	index: number;
	session: any;
	dispatch: Function;
	isEditor: boolean;
	profile_context: StateInterface["profile_context"];
	property_snackbar: PropertySnackbarInterface;
}) => {
	const theme = useTheme();

	const input = React.useRef<HTMLInputElement>(null);

	const router = React.useRef(useRouter()).current;

	const [selectedFiles, setSelectedFiles] = React.useState<Array<FileAndMore>>([]);

	const [percentage, setPercentage] = React.useState<Array<number>>([0]);

	const [stage, setStage] = React.useState<Array<string>>(["NOT_STARTED"]);

	const allFilesFetched = stage.every((status) => status === "FETCHED_FILE_INFORMATION");

	const [fileId, setFileId] = React.useState<Array<ObjectId | null>>([null]);

	const [loading, setLoading] = React.useState<boolean>(false);

	// const [editProfileModal, setEditProfileModal] = React.useState(false);

	const [openSnackbar, setOpenSnackbar] = React.useState(false);

	const [selectedFilePreview, setSelectedFilePreview] = React.useState<Array<string>>([]);

	const [fileErrorSnackbarText, setFileErrorSnackbarText] = React.useState<string>("File size or type not supported");

	const [category, setCategory] = React.useState<Array<string>>([]);

	const [load, setLoad] = React.useState<boolean>(false);

	const [isFileDialogOpen, setFileDialogOpen] = React.useState(false);

	const [alertModal, setAlertModal] = React.useState<boolean>(false);

	const [step, setStep] = React.useState<number>(0);

	const localLang = {
		sizeOrTypeNotSupported:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ಫೈಲ್ ಗಾತ್ರ ಅಥವಾ ಪ್ರಕಾರ ಬೆಂಬಲಿಸಲ್ಪಟ್ಟಿಲ್ಲ "
					: "File size or type not supported",
		isAn: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಒಂದು" : " is an",
		are: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಇವು" : " are",
		unsupported: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಅಸಮರ್ಥವಾಗಿದೆ" : " unsupported",
		file: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಫೈಲ್" : " file",
		files: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಫೈಲ್ಗಳು" : " files",
		and: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಮತ್ತು" : " and",
		has: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಹೊಂದಿದೆ" : " has",
		have: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಹೊಂದಿದುವು" : " have",
		beenDeselected:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? " ಸ್ವಯಂಚಾಲಿತವಾಗಿ ರದ್ದುಗೊಂಡಿದೆ."
					: " been deselected automatically.",
		jpegOrPng: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "JPEG ಅಥವಾ PNG" : "JPEG or PNG",
		imgMaxSize:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಚಿತ್ರ ಗಾತ್ರ ಗರಿಷ್ಠ 10MB" : "Max upload 10MB",
		category: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ವರ್ಗ" : "Category",
		exterior: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಬಾಹ್ಯ" : "Exterior",
		interior: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಆಂತರಿಕ" : "Interior",
		masterPlan: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಮಾಸ್ಟರ್ ಪ್ಲಾನ್" : "Master plan",
		amenities: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಸೌಲಭ್ಯಗಳು" : "Amenities",
		routMap: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ರೂಟ್ ಮ್ಯಾಪ್" : "Route map",
		addMore: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಹೆಚ್ಚು ಸೇರಿಸಿ" : "Add More",
		addPhotosFromYourGallary:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಗ್ಯಾಲರಿ ನಿಂದ ಫೋಟೋಗಳನ್ನು ಸೇರಿಸಿ"
					: "Add photos from your gallery",
		maxLimit:
			router.locale === "ar-AE"
				? "ملف JPG أو PNG، بحجم لا يزيد عن 10 ميجابايت."
				: router.locale === "kn-IN"
					? "JPG ಅಥವಾ PNG ಫೈಲ್, ಗಾತ್ರವು 10MB ಗಿಂತ ಕಡಿಮೆ ಇರಬೇಕು."
					: "JPG or PNG, file size no more than 10MB",
		moreThePixelMoreTheReach:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ಹೆಚ್ಚು ಪಿಕ್ಸೆಲ್ ಹೆಚ್ಚು ತಲುಪುತ್ತದೆ"
					: "More the pixels more the reach",
		startUploading:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಅಪ್ಲೋಡ್ ಪ್ರಾರಂಭಿಸಿ" : "Start Uploading",
		coverImageText:
			router.locale === "ar-AE"
				? "اضغط على الصورة المحددة لجعلها صورة الغلاف"
				: router.locale === "kn-IN"
					? "ಆಯ್ದ ಚಿತ್ರದ ಮೇಲೆ ಟ್ಯಾಪ್ ಮಾಡಿ, ಅದನ್ನು ಕವರ್ ಚಿತ್ರವನ್ನಾಗಿ ಮಾಡಿರಿ"
					: "Tap on selected image to make it cover image",
	};

	/*

	& Next, let's declare the priority state. This state will be used to hold the index number of the cover image selected by the user .

	*/

	const [priority, setPriority] = React.useState<number>(0);
	const [prioritySelected, setPrioritySelected] = React.useState<boolean>(false);

	/*

	& Let's declare the router. Here we are using the useRouter hook from nextjs.

	& We are using the router to get the query params from the url. Memoize the router object to prevent unnecessary re-renders.

	*/

	interface UploadProgress {
		fileId: ObjectId | null;
		percentage: number;
		stage: string;
	}

	const handleUpload = async (validFiles: Array<FileAndMore>, filePreview: Array<string>) => {
		setLoad(true);
		await uploadFiles(
			session ? (session.authorized ? (session.session_id ? session.session_id : "") : "") : "",
			validFiles.map((file: FileAndMore) => ({
				file: file.file,
				mime_type: file.mime_type,
				original_file_id: new ObjectId(),
				original_file_server_path: "",
				original: true,
				is_business_profile: propertyData
					? propertyData.created_by
						? propertyData.created_by.is_business_profile
						: false
					: false,
				business_profile_id: propertyData
					? propertyData.created_by
						? propertyData.created_by.business_profile_id
							? new ObjectId((propertyData.created_by.business_profile_id as BusinessProfileInterface)._id)
							: new ObjectId()
						: new ObjectId()
					: new ObjectId(),
				entity: "property_" + (propertyData ? (propertyData.is_ofb ? "ofb" : "regular") : "regular"),
				entity_id: propertyData ? new ObjectId(propertyData._id) : new ObjectId(),
				use_case: "property_image",
				uiPath: "/",
				isPublic: false,
				text: propertyData.created_by.is_business_profile
					? (propertyData.created_by.business_profile_id as BusinessProfileInterface).title
					: (propertyData.created_by.user_id as UserInterface).first_name +
						" " +
						(propertyData.created_by.user_id as UserInterface).last_name,
			})),
			(uploadProgress: Array<UploadProgress>) => {
				const data = uploadProgress.map((progress) => progress.fileId);

				const uploadedProg = uploadProgress.map((progress) => progress);

				const stageInfo = uploadProgress.map((progress) => progress.stage);

				const percentageInfo = uploadProgress.map((progress) => progress.percentage);

				fileId[0] != null ? setFileId([...fileId, ...data]) : setFileId(data);

				percentage[0] != 0 ? setPercentage([...percentage, ...percentageInfo]) : setPercentage(percentageInfo);

				if (uploadedProg.map((progress) => progress.stage).includes("FETCHED_FILE_INFORMATION")) {
					stage[0] != "NOT_STARTED" ? setStage([...stage, ...stageInfo]) : setStage(stageInfo);
				}
			},
		);
		setLoad(false);
	};

	const handleSubmit = async () => {
		setLoading(true);
		const property_id: ObjectId | string = propertyData._id;
		await dispatch(
			addPropertyImagesThunk({
				property_id,
				file_id: fileId,
				priorityIndex: priority,
				isPrioritySelected: prioritySelected,
				category: category,
				phoneNumber: session ? session.user.dbUser.phone : "",
				countryCode: session ? session.user.dbUser.country_code : "",
				mime_type: selectedFiles.map((file: FileAndMore) => file.mime_type),
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				editor_is_business_profile: isEditor
					? profile_context.is_business_profile
					: propertyData.created_by.is_business_profile,
				editor_user_id: isEditor ? profile_context.user_id : (propertyData.created_by.user_id as UserInterface)._id,
				editor_business_profile_id: isEditor
					? profile_context.business_profile_id
					: (propertyData.created_by.business_profile_id as BusinessProfileInterface)._id,
				url: router.asPath ? router.asPath : "",
				profile_context: profile_context,
			}),
		);
		setSelectedFiles([]);
		setSelectedFilePreview([]);
		setLoading(false);
		setStage(["NOT_STARTED"]);
		setFileId([null]);
		setPercentage([0]);
		handleUploadClose();
	};
	const handleRemoveImage = async (index: number) => {
		const newSelectedFilePreview = [...selectedFilePreview];
		const newSelectedFiles = [...selectedFiles];
		const newFileId = [...fileId];
		const newStage = [...stage];

		newSelectedFilePreview.splice(index, 1);
		newSelectedFiles.splice(index, 1);
		newFileId.splice(index, 1);
		newStage.splice(index, 1);

		// Update the state with the new arrays
		setSelectedFilePreview(newSelectedFilePreview);
		setSelectedFiles(newSelectedFiles);
		setFileId(newFileId);
		setStage(newStage);
	};

	const handleUploadClose = () => {
		setAlertModal(false);
		setSelectedFiles([]);
		setSelectedFilePreview([]);
		setLoading(false);
		setStage(["NOT_STARTED"]);
		setCategory(["all_images"]);
		setFileId([null]);
		setPercentage([0]);
		handleClose();
	};

	const handleSelect = (event: SelectChangeEvent, index: number) => {
		if (event.target.value !== "" && event.target.value !== undefined) {
			setCategory((prev) => {
				const updated = [...prev];
				updated[index] = event.target.value.replace(" ", "_").toLowerCase();
				return updated;
			});
		} else {
			setCategory((prev) => {
				const updated = [...prev];
				updated[index] = "exterior";
				return updated;
			});
		}
	};

	const formatCategory = (category: string) => {
		return category
			.split("_")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(" ");
	};

	React.useEffect(() => {
		if (open) {
			setFileDialogOpen(true);
		}
	}, [open]);

	React.useEffect(() => {
		if (isFileDialogOpen && input.current) {
			input.current.value = "";
			input.current.click();
			// Set isFileDialogOpen to false after opening the dialog
			setFileDialogOpen(false);
		}
	}, [isFileDialogOpen]);

	return (
		<React.Fragment>
			<Snack
				open={openSnackbar}
				autoHideDuration={3000}
				onClose={() => setOpenSnackbar(false)}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => setOpenSnackbar(false)}
					severity="error"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{fileErrorSnackbarText}
				</Alert>
			</Snack>

			<Snack
				open={property_snackbar ? (property_snackbar.property_images_upload as boolean) : false}
				autoHideDuration={3000}
				onClose={() => dispatch(setPropertySnackbarThunk(false))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => dispatch(setPropertySnackbarThunk(false))}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					Uploaded successfully
				</Alert>
			</Snack>

			{/* Alert Modal */}

			<CustomDialog
				open={alertModal}
				maxWidth="xs"
				fullWidth={true}
				onClose={() => {
					setAlertModal(false);
				}}
			>
				<DialogTitle>Are you sure you want to close?</DialogTitle>
				<DialogContent>
					<DialogContentText>All selected images will be lost</DialogContentText>
				</DialogContent>
				<DialogActions>
					<BeegruButton
						onClick={() => {
							handleUploadClose();
							setAlertModal(false);
						}}
						color="primary"
						variant="outlined"
					>
						Yes
					</BeegruButton>
					<BeegruButton
						onClick={() => {
							setAlertModal(false);
						}}
						color="primary"
						variant="contained"
					>
						No
					</BeegruButton>
				</DialogActions>
			</CustomDialog>

			{/* Upload Image Modal */}

			<CustomDialog
				open={open}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth={true}
				disableScrollLock={true}
				PaperProps={{ sx: { borderRadius: "1rem" } }}
			>
				<CustomDialogTitle>
					Upload images
					<IconButton
						aria-label="close"
						onClick={() => {
							selectedFiles.length !== 0 ? setAlertModal(true) : handleUploadClose();
						}}
					>
						{loading ? null : <Close />}
					</IconButton>
				</CustomDialogTitle>

				<CustomDialogContent>
					<InputContent
						ref={input}
						type="file"
						accept="image/jpeg, image/png"
						multiple={true}
						onChange={async (event) => {
							const files = event.target.files;
							const allowedTypes = ["image/jpeg", "image/png"];
							const allowedExtensions = ["jpg", "jpeg", "png"];
							const uploadType = "property_or_services";
							if (!files?.length) return setOpenSnackbar(true);

							const { validFiles, invalidFiles } = await validateFiles(
								Array.from(files),
								allowedTypes,
								allowedExtensions,
								uploadType,
							);

							if (validFiles.length) {
								const previews = validFiles.map((f) => URL.createObjectURL(f.file));
								setSelectedFiles((prev) => [...prev, ...validFiles]);
								handleUpload(validFiles, [...selectedFilePreview, ...previews]);
								setSelectedFilePreview((prev) => [...prev, ...previews]);
								setStep(0);
							}

							if (invalidFiles.length) {
								const names = invalidFiles.map((f) => f.name).join(", ");
								const display = names.length > 15 ? names.slice(0, 15) + "..." : names;
								setFileErrorSnackbarText(
									`${display} ${invalidFiles.length === 1 ? localLang.isAn : localLang.are}` +
										`${localLang.unsupported}${invalidFiles.length === 1 ? localLang.file : localLang.files}` +
										`${localLang.and}${invalidFiles.length === 1 ? localLang.has : localLang.have}${localLang.beenDeselected}`,
								);
								setOpenSnackbar(true);
							}
						}}
					/>

					{selectedFiles.length === 0 && (
						<div style={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
							<UploadContent onClick={() => input.current?.click()}>
								<SvgIcon
									component={RequestImagesIcon}
									viewBox="0 0 41 33"
									width={24}
									height={24}
									sx={{ width: "2.563rem", height: "1.976rem", fill: "none" }}
								/>
								<Typography sx={{ color: theme.palette.text.primary, fontSize: "0.875rem" }}>
									{localLang.addPhotosFromYourGallary}
								</Typography>
								<Typography sx={{ color: theme.palette.text.secondary, fontSize: "0.75rem" }}>
									{localLang.maxLimit}
								</Typography>
							</UploadContent>

							{/* <DividerContainer>
								<Divider style={{ flex: 1 }} />
								<span style={{ padding: "0 8px", fontSize: "1rem", color: "#333" }}>
									{router.locale === "ar-AE" ? "أو" : "OR"}
								</span>
								<Divider style={{ flex: 1 }} />
							</DividerContainer>

							<UploadContent sx={{ flexDirection: "row", gap: "1rem" }}>
								<PhotoCameraOutlinedIcon />
								<Typography sx={{ color: "#9E9E9E", fontSize: "0.875rem" }}>
									{localLang.addPhotosFromYourGallary}
								</Typography>
							</UploadContent> */}
						</div>
					)}

					<ImagePreviewContainer>
						{selectedFilePreview.length > 0 && allFilesFetched && (
							<div
								style={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "space-between",
									alignItems: "center",
									width: "100%",
								}}
							>
								<Typography
									sx={{
										color: theme.palette.text.primary,
										fontSize: "1rem",
										fontWeight: 700,
									}}
								>
									Uploaded {selectedFilePreview.length} {selectedFilePreview.length > 1 ? "images" : "image"}
								</Typography>
								{/* <IconButton
										onClick={() => {
											input.current?.click();
										}}
									>
										<AddOutlinedIcon />
									</IconButton>
									 */}
								<BeegruButton
									variant="contained"
									sx={{
										color: theme.palette.primary.main,
										fontSize: "0.875rem",
										fontWeight: 500,
										textTransform: "none",
									}}
									onClick={() => {
										input.current?.click();
									}}
								>
									<AddOutlinedIcon /> Add more
								</BeegruButton>
							</div>
						)}
						{selectedFilePreview.length > 0 && allFilesFetched && (
							<div style={{ display: "flex", position: "relative", width: "100%" }}>
								<Chip
									label="Cover photo"
									sx={{
										position: "absolute",
										top: "0.5rem",
										left: "0.5rem",
										backgroundColor: "#FFF",
										color: "#7E7D7D",
										fontSize: "0.75rem",
										fontWeight: 400,
										borderRadius: "0.75rem",
										boxShadow: "0px 4px 18px rgba(0,0,0,0.05)",
										height: "20px",
										zIndex: 1,
									}}
								/>
								<PreviewImage
									src={selectedFilePreview[priority]}
									alt="Property Images"
									referrerPolicy="no-referrer"
									style={{ width: "100%", height: "10.5rem", borderRadius: "16px" }}
								/>
							</div>
						)}

						{selectedFilePreview.map((file: string, index: number) => (
							<ContentContainer key={index}>
								{file && (
									<ImageContainer
										onClick={() => {
											setPriority(index);
											setPrioritySelected(true);
										}}
									>
										<PreviewImage
											alt="Property Images"
											src={file}
											referrerPolicy="no-referrer"
										/>
										<ProgressContainer>
											{(stage[index] === undefined ||
												stage[index] === "NOT_STARTED" ||
												(stage[index] && stage[index] !== "FETCHED_FILE_INFORMATION")) && (
												<CircularProgress
													// variant={percentage[index] !== 0 ? "determinate" : "indeterminate"}
													// value={percentage[index]}
													sx={{ color: "#FFF" }}
												/>
											)}
										</ProgressContainer>
										{stage[index] === "FETCHED_FILE_INFORMATION" && (
											<IconButton
												size="small"
												onClick={() => handleRemoveImage(index)}
												sx={{
													position: "absolute",
													top: "0.3rem",
													right: "0.5rem",
													background: "#FFFFFF90",
													padding: "0.5rem",
													height: "2rem",
													width: "2rem",
													zIndex: 1,
													margin: "0.5rem 0rem 0rem 0rem",
												}}
											>
												<DeleteOutline sx={{ color: "#E31B0C", fontSize: "1.25rem" }} />
											</IconButton>
										)}
										{/* Select category */}
										<FormControlContainer size="small">
											<Select
												value={category[index] ? formatCategory(category[index]) : "Exterior"}
												displayEmpty
												renderValue={(selected) => {
													if (selected === "all_images") {
														return <em style={{ fontSize: "0.875rem" }}>Category</em>;
													}

													return selected;
												}}
												inputProps={{
													placeholder: "Category",
												}}
												onChange={(event) => handleSelect(event, index)}
												sx={{
													width: "10rem",
													backgroundColor: "#FFFFFF",
												}}
												MenuProps={{
													disableScrollLock: true,
													MenuListProps: {
														sx: {
															backgroundColor: theme.palette.background.paper,
														},
													},
												}}
											>
												<MenuItem value={"Exterior"}>Exterior</MenuItem>
												<MenuItem value={"Interior"}>Interior</MenuItem>
												<MenuItem value={"Master Plan"}>Master Plan</MenuItem>
												<MenuItem value={"Amenities"}>Amenities</MenuItem>
												<MenuItem value={"Route Map"}>Route Map</MenuItem>
											</Select>
										</FormControlContainer>
									</ImageContainer>
								)}
							</ContentContainer>
						))}
					</ImagePreviewContainer>
				</CustomDialogContent>
				<CustomDialogActions>
					{stage.every((stage) => stage === "FETCHED_FILE_INFORMATION") ? (
						loading ? (
							<CircularProgress />
						) : (
							<BeegruButton
								disabled={selectedFiles.length === 0 ? true : false}
								variant="contained"
								flavor="primary"
								size="large"
								onClick={(event: React.MouseEvent<HTMLElement>) => {
									handleSubmit();
								}}
								name="upload"
								type="button"
							>
								Save
							</BeegruButton>
						)
					) : null}
				</CustomDialogActions>
			</CustomDialog>
		</React.Fragment>
	);
};

export default UploadImageModal;
